import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import {
  fetchDashboardSummary,
  fetchBills,
  setCurrentProjectId,
} from '../store/slices/financeSlice'
import { PaymentStatusBadge, FinanceAuditLog } from '../components/finance'
import usePermissions, { Permission } from '../hooks/usePermissions'
import {
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  TruckIcon,
  DocumentTextIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

const FinanceDashboard = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    dashboard,
    dashboardLoading,
    dashboardError,
    bills,
    billsLoading,
  } = useAppSelector(state => state.finance)

  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(setCurrentProjectId(currentProject.id))
      dispatch(fetchDashboardSummary({ projectId: currentProject.id, billingPeriod }))
      dispatch(fetchBills({ projectId: currentProject.id, params: { billingPeriod, limit: 10 } }))
    }
  }, [dispatch, currentProject?.id, billingPeriod])

  const handleRefresh = () => {
    if (currentProject?.id) {
      dispatch(fetchDashboardSummary({ projectId: currentProject.id, billingPeriod }))
      dispatch(fetchBills({ projectId: currentProject.id, params: { billingPeriod, limit: 10 } }))
    }
  }

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0'
    }
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const { hasPermission } = usePermissions(currentProject?.id)

  const navigationItems = [
    {
      name: '聯絡人管理',
      href: `/project/${projectKey}/finance/contacts`,
      icon: UserGroupIcon,
      description: '管理住戶聯絡人資料',
      permission: Permission.VIEW_FINANCE_CONTACTS,
    },
    {
      name: '房屋單位',
      href: `/project/${projectKey}/finance/units`,
      icon: BuildingOfficeIcon,
      description: '管理房屋單位與繳費設定',
      permission: Permission.VIEW_FINANCE_UNITS,
    },
    {
      name: '停車位管理',
      href: `/project/${projectKey}/finance/parking`,
      icon: TruckIcon,
      description: '管理停車位分配',
      permission: Permission.VIEW_FINANCE_PARKING,
    },
    {
      name: '帳單管理',
      href: `/project/${projectKey}/finance/bills`,
      icon: DocumentTextIcon,
      description: '產生與管理月費帳單',
      permission: Permission.VIEW_FINANCE_BILLS,
    },
    {
      name: '銀行對帳',
      href: `/project/${projectKey}/finance/reconciliation`,
      icon: BanknotesIcon,
      description: '上傳銀行檔案進行對帳',
      permission: Permission.VIEW_FINANCE_RECONCILIATION,
    },
    {
      name: '廠商管理',
      href: `/project/${projectKey}/finance/vendors`,
      icon: BuildingStorefrontIcon,
      description: '收支廠商與銀行帳戶資料',
      permission: Permission.VIEW_FINANCE_VENDORS,
    },
    {
      name: '財務設定',
      href: `/project/${projectKey}/finance/settings`,
      icon: Cog6ToothIcon,
      description: '費率與系統設定',
      permission: Permission.VIEW_FINANCE_SETTINGS,
    },
  ].filter(item => hasPermission(item.permission))

  if (dashboardLoading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <HomeIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">財務管理</h1>
            <p className="text-sm text-gray-500">社區管理費收繳系統</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div>
            <label className="sr-only">帳單期間</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <button
            onClick={handleRefresh}
            disabled={dashboardLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${dashboardLoading ? 'animate-spin' : ''}`} />
            重新整理
          </button>
        </div>
      </div>

      {dashboardError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{dashboardError}</p>
        </div>
      )}

      {/* Quick Navigation - 移到最上方 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">功能導覽</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          {navigationItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="flex items-center p-6 hover:bg-gray-50 transition-colors"
            >
              <item.icon className="w-8 h-8 text-gray-400" />
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-900">{item.name}</p>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            </Link>
          ))}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingOfficeIcon className="w-8 h-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">總戶數</p>
              <p className="text-2xl font-semibold text-gray-900">
                {dashboard?.totalUnits || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">$</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">已繳</p>
              <p className="text-2xl font-semibold text-green-600">
                {dashboard?.paidCount || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 font-bold">!</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">未繳</p>
              <p className="text-2xl font-semibold text-red-600">
                {dashboard?.unpaidCount || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 font-bold">?</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">待審核</p>
              <p className="text-2xl font-semibold text-yellow-600">
                {dashboard?.pendingCount || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Unlinked Parking Spaces Alert */}
      {dashboard?.alerts?.unlinkedParkingSpaces && dashboard.alerts.unlinkedParkingSpaces.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-amber-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
              <h2 className="text-base font-medium text-amber-800">
                車位未關聯戶號
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-200 text-amber-800">
                  {dashboard.alerts.unlinkedParkingSpaces.length} 筆
                </span>
              </h2>
            </div>
            <p className="text-xs text-amber-600">這些車位的停車費不會出現在任何帳單中</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-amber-200">
              <thead className="bg-amber-100/50 sticky top-0">
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-medium text-amber-700">車位編號</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-amber-700">類型</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-amber-700">所有人</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-amber-700">車牌</th>
                  <th className="px-6 py-2 text-right text-xs font-medium text-amber-700">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {dashboard.alerts.unlinkedParkingSpaces.map((space) => {
                  const typeLabel = space.type === 'CAR' ? '汽車位' : space.type === 'MOTOR' ? '機車位' : '腳踏車位'
                  return (
                    <tr key={space.id} className="hover:bg-amber-100/40 transition-colors">
                      <td className="px-6 py-2.5 text-sm font-medium text-gray-900">{space.spaceCode}</td>
                      <td className="px-6 py-2.5 text-sm text-gray-600">{typeLabel}</td>
                      <td className="px-6 py-2.5 text-sm text-gray-600">{space.owner?.name || '-'}</td>
                      <td className="px-6 py-2.5 text-sm text-gray-500">
                        {space.licensePlates?.length > 0 ? space.licensePlates.join(', ') : '-'}
                      </td>
                      <td className="px-6 py-2.5 text-right">
                        <Link
                          to={`/project/${projectKey}/finance/parking?edit=${space.id}`}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded hover:bg-amber-200 transition-colors"
                        >
                          編輯關聯
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Collection Progress */}
      {dashboard && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">收款進度</h2>
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-gray-600">
              <span>已收款: {formatCurrency(dashboard.totalReceived)}</span>
              <span>應收款: {formatCurrency(dashboard.totalExpected)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(dashboard.collectionRate ?? 0, 100)}%` }}
              />
            </div>
            <div className="text-center text-lg font-semibold text-blue-600">
              收繳率: {(dashboard.collectionRate ?? 0).toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      {/* Recent Bills */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">近期帳單</h2>
          <Link
            to={`/project/${projectKey}/finance/bills`}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            查看全部
          </Link>
        </div>

        {billsLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : bills.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            本期尚無帳單資料
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單位
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    期間
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    金額
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bills.slice(0, 10).map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {bill.unit?.unitCode || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {bill.billingPeriod}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(bill.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <PaymentStatusBadge status={bill.paymentStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 財務操作紀錄 */}
      <FinanceAuditLog
        title="財務操作紀錄（最近 30 筆）"
        defaultOpen={false}
        limit={30}
      />
    </div>
  )
}

export default FinanceDashboard
